@file:Suppress("MatchingDeclarationName")

package ai.jessie.web.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import org.jetbrains.compose.web.dom.Span

/**
 * Outline is the default; Solid fills closed shapes (`star`, `play`, `pause`) for active states.
 */
enum class IconStyle(val fill: String) {
    REGULAR("none"),
    SOLID("currentColor"),
}

/**
 * An inline SVG icon by name (`calendar-alt`, `clock`, `chevron-right`, …), drawn in the current
 * text colour on a 24-unit grid with a 2-unit outline. Decorative unless a [label] is given.
 */
@Composable
fun icon(
    name: String,
    style: IconStyle = IconStyle.REGULAR,
    label: String? = null,
    size: Int = 16,
) {
    // key: `ref` runs once per element, so changed glyph inputs (the colour-mode toggle) need a new
    // one.
    key(name, style, size) { svgIcon(name, style, label, size) }
}

@Composable
private fun svgIcon(name: String, style: IconStyle, label: String?, size: Int) {
    Span(
        attrs = {
            if (label == null) attr("aria-hidden", "true")
            else {
                attr("role", "img")
                attr("aria-label", label)
            }
            attr("style", "display: inline-flex; flex-shrink: 0; vertical-align: -0.125em")
            // Markup comes only from the constant ICONS table below, never from user input.
            ref { element ->
                element.innerHTML =
                    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="$size" """ +
                        """height="$size" fill="${style.fill}" stroke="currentColor" stroke-width="2" """ +
                        """stroke-linecap="round" stroke-linejoin="round">${ICONS[name].orEmpty()}</svg>"""
                onDispose {}
            }
        }
    )
}

/*
 * Glyphs follow Lucide (https://lucide.dev), ISC License: Copyright (c) for portions of Lucide are
 * held by Cole Bemis 2013-2022 as part of Feather (MIT). All other copyright (c) for Lucide are held
 * by Lucide Contributors 2022. Permission to use, copy, modify, and/or distribute this software for
 * any purpose with or without fee is hereby granted, provided that the above copyright notice and
 * this permission notice appear in all copies. THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR
 * DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
 * INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR
 * PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR
 * IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * Names keep the former Font Awesome 5 names so callers did not change.
 */
internal val ICONS: Map<String, String> =
    mapOf(
        "archive" to
            """<rect x="3" y="3" width="18" height="4" rx="1"/>""" +
                """<path d="M5 7v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7M10 12h4"/>""",
        "building" to
            """<rect x="4" y="2" width="16" height="20" rx="2"/>""" +
                """<path d="M9 22v-4h6v4M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01""" +
                """M8 14h.01M12 14h.01M16 14h.01"/>""",
        "calculator" to
            """<rect x="4" y="2" width="16" height="20" rx="2"/>""" +
                """<path d="M8 6h8M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01""" +
                """M16 14v4M8 18h.01M12 18h.01"/>""",
        "calendar-alt" to
            """<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>""",
        "check" to """<path d="M20 6 9 17l-5-5"/>""",
        "chevron-right" to """<path d="m9 18 6-6-6-6"/>""",
        "clock" to """<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>""",
        "comment-alt" to
            """<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>""",
        "download" to
            """<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>""",
        "ellipsis-v" to
            """<circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/>""" +
                """<circle cx="12" cy="19" r="1"/>""",
        "file-pdf" to
            """<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>""" +
                """<path d="M14 2v6h6"/>""",
        "folder" to
            """<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0""" +
                """ 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>""",
        "inbox" to
            """<path d="m4 4-2 8v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-2-8Z"/>""" +
                """<path d="M2 12h6l2 3h4l2-3h6"/>""",
        "moon" to """<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>""",
        "pause" to
            """<rect x="6" y="4" width="4" height="16" rx="1"/>""" +
                """<rect x="14" y="4" width="4" height="16" rx="1"/>""",
        "play" to """<polygon points="6 3 20 12 6 21"/>""",
        "plus" to """<path d="M12 5v14M5 12h14"/>""",
        "search" to """<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>""",
        "star" to
            """<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02""" +
                """ 7 14.14 2 9.27 8.91 8.26"/>""",
        "sun" to
            """<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41""" +
                """M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>""",
        "times" to """<path d="M18 6 6 18M6 6l12 12"/>""",
        "times-circle" to """<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/>""",
        "trash-alt" to
            """<path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0""" +
                """ 0 1 2 2v2"/>""",
        "upload" to
            """<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>""",
        "user" to
            """<circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/>""",
        "user-circle" to
            """<circle cx="12" cy="12" r="10"/><circle cx="12" cy="10" r="3"/>""" +
                """<path d="M7 20.66V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.66"/>""",
    )
